import { TimelineData } from '@/features/timeline';
import { useEffect, useRef, useState } from 'react';
import { DateRange } from '../../models/timeline-calendar.types';
import {
  AUTO_PAN_EDGE_THRESHOLD_PX,
  AUTO_PAN_MAX_MS_PER_FRAME,
  CREATE_DRAG_THRESHOLD_PX,
  MOVE_DRAG_THRESHOLD_PX,
  TOUCH_PAN_THRESHOLD_PX,
} from './constants';
import {
  getSnapGranularity,
  normalizeDurationRange,
  normalizeEditableRange,
  resolveResizedRange,
  shiftDateByMs,
  snapToGranularity,
} from './date-utils';
import {
  AddMode,
  CreateDraft,
  FlatTimelineItem,
  MovePreviewState,
  ResizePreviewState,
} from './types';

interface CreatePointerState {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startDate: string;
  sectionId: string;
  lastDate: string;
  isDragging: boolean;
}

interface ResizePointerState {
  pointerId: number;
  sectionId: string;
  itemId: string;
  edge: 'start' | 'end';
  initialRange: CreateDraft;
}

interface MovePointerState {
  pointerId: number;
  sectionId: string;
  itemId: string;
  initialRange: CreateDraft;
  baseDate: string;
  lastDate: string;
  startClientX: number;
  startClientY: number;
  isDragging: boolean;
}

interface UseTimelineCalendarInteractionsParams {
  isReadOnly: boolean;
  timelines: TimelineData['pages'][number]['sections'];
  dateRange: DateRange;
  zoomSurfaceRef: React.RefObject<HTMLDivElement>;
  timelineItemsRef: React.RefObject<HTMLDivElement>;
  sectionItemById: Map<string, FlatTimelineItem>;
  itemsWithColors: Array<{ id: string }>;
  selectedItemId: string | null;
  handleItemClick: (itemId: string) => void;
  handleItemHover: (hoverState: { itemId: string; clientX: number; clientY: number } | null) => void;
  zoomByScale: (scale: number) => void;
  panByMs: (deltaMs: number) => void;
  onItemTitleChange?: (sectionId: string, itemId: string, nextTitle: string) => void;
  onItemCreate?: (sectionId: string, draft: { title: string; startDate: string; endDate?: string }) => void;
  onItemDateRangeChange?: (
    itemId: string,
    nextRange: { sectionId: string; startDate: string; endDate?: string },
  ) => void;
}

export const useTimelineCalendarInteractions = ({
  isReadOnly,
  timelines,
  dateRange,
  zoomSurfaceRef,
  timelineItemsRef,
  sectionItemById,
  itemsWithColors,
  selectedItemId,
  handleItemClick,
  handleItemHover,
  zoomByScale,
  panByMs,
  onItemTitleChange,
  onItemCreate,
  onItemDateRangeChange,
}: UseTimelineCalendarInteractionsParams) => {
  const prevReadOnlyRef = useRef(isReadOnly);
  const pinchStateRef = useRef<{ distance: number } | null>(null);
  const touchPanStateRef = useRef<{
    startClientX: number;
    lastClientX: number;
    isDragging: boolean;
  } | null>(null);
  const pendingZoomRef = useRef<{ scale: number } | null>(null);
  const zoomRafRef = useRef<number | null>(null);
  const panRafRef = useRef<number | null>(null);
  const pendingPanMsRef = useRef(0);
  const creationPointerStateRef = useRef<CreatePointerState | null>(null);
  const resizePointerStateRef = useRef<ResizePointerState | null>(null);
  const movePointerStateRef = useRef<MovePointerState | null>(null);
  const autoPanRafRef = useRef<number | null>(null);
  const autoPanStrengthRef = useRef(0);

  const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingTitleDraft, setEditingTitleDraft] = useState('');
  const [addMode, setAddMode] = useState<AddMode>('none');
  const [pendingCreateDraft, setPendingCreateDraft] = useState<CreateDraft | null>(null);
  const [pendingCreateTitle, setPendingCreateTitle] = useState('');
  const [dragPreviewDraft, setDragPreviewDraft] = useState<CreateDraft | null>(null);
  const [hoverPreviewDraft, setHoverPreviewDraft] = useState<CreateDraft | null>(null);
  const [resizePreviewState, setResizePreviewState] = useState<ResizePreviewState | null>(null);
  const [movePreviewState, setMovePreviewState] = useState<MovePreviewState | null>(null);
  const [previewClientY, setPreviewClientY] = useState<number | null>(null);

  const isCreateFlowActive = addMode !== 'none' || pendingCreateDraft !== null || dragPreviewDraft !== null;
  const isResizeFlowActive = resizePreviewState !== null;
  const isMoveFlowActive = movePreviewState !== null;

  const cancelItemTitleEditing = () => {
    setEditingItemId(null);
    setEditingTitleDraft('');
  };

  const startItemTitleEditing = (itemId: string, currentTitle: string) => {
    if (isReadOnly) {
      return;
    }
    if (selectedItemId !== itemId) {
      handleItemClick(itemId);
    }
    handleItemHover(null);
    setPendingCreateDraft(null);
    setPendingCreateTitle('');
    setDragPreviewDraft(null);
    setAddMode('none');
    setEditingItemId(itemId);
    setEditingTitleDraft(currentTitle);
  };

  const commitItemTitleEditing = () => {
    if (!editingItemId) {
      return;
    }

    const targetItem = sectionItemById.get(editingItemId);
    if (!targetItem) {
      cancelItemTitleEditing();
      return;
    }

    if (targetItem.title !== editingTitleDraft) {
      onItemTitleChange?.(targetItem.sectionId, editingItemId, editingTitleDraft);
    }
    cancelItemTitleEditing();
  };

  const cancelPendingCreate = () => {
    setPendingCreateDraft(null);
    setPendingCreateTitle('');
    setDragPreviewDraft(null);
    setPreviewClientY(null);
    setAddMode('none');
  };

  const commitPendingCreate = () => {
    if (isReadOnly) {
      cancelPendingCreate();
      return;
    }

    if (!pendingCreateDraft) {
      return;
    }

    const title = pendingCreateTitle.trim();

    const fallbackSectionId = timelines[0]?.id;
    const sectionId = pendingCreateDraft.sectionId ?? fallbackSectionId;
    if (!sectionId) {
      cancelPendingCreate();
      return;
    }

    onItemCreate?.(sectionId, {
      title,
      startDate: pendingCreateDraft.startDate,
      endDate: pendingCreateDraft.endDate,
    });
    cancelPendingCreate();
  };

  const calculateDistance = (touches: React.TouchList): number => {
    const first = touches[0];
    const second = touches[1];
    const dx = second.clientX - first.clientX;
    const dy = second.clientY - first.clientY;
    return Math.hypot(dx, dy);
  };

  const scheduleZoom = (scale: number) => {
    pendingZoomRef.current = { scale };
    if (zoomRafRef.current !== null) {
      return;
    }

    zoomRafRef.current = window.requestAnimationFrame(() => {
      zoomRafRef.current = null;
      const pendingZoom = pendingZoomRef.current;
      pendingZoomRef.current = null;
      if (!pendingZoom) {
        return;
      }
      zoomByScale(pendingZoom.scale);
    });
  };

  const schedulePan = (deltaMs: number) => {
    if (!Number.isFinite(deltaMs) || deltaMs === 0) {
      return;
    }

    pendingPanMsRef.current += deltaMs;
    if (panRafRef.current !== null) {
      return;
    }

    panRafRef.current = window.requestAnimationFrame(() => {
      panRafRef.current = null;
      const pendingPanMs = pendingPanMsRef.current;
      pendingPanMsRef.current = 0;
      if (pendingPanMs !== 0) {
        panByMs(pendingPanMs);
      }
    });
  };

  const dateFromClientX = (clientX: number): string | null => {
    const itemsArea = timelineItemsRef.current;
    if (!itemsArea) {
      return null;
    }

    const rect = itemsArea.getBoundingClientRect();
    const contentWidth = Math.max(itemsArea.clientWidth, 1);
    const relativeX = clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, relativeX / contentWidth));
    const spanMs = Math.max(dateRange.end.getTime() - dateRange.start.getTime(), 1);
    const timestamp = dateRange.start.getTime() + (spanMs * ratio);
    const granularity = getSnapGranularity(spanMs);
    return snapToGranularity(new Date(timestamp), granularity);
  };

  const resolveSectionIdByY = (clientY: number): string | null => {
    const itemsArea = timelineItemsRef.current;
    const fallbackSectionId = timelines[0]?.id ?? null;
    if (!itemsArea || timelines.length === 0) {
      return fallbackSectionId;
    }

    const rect = itemsArea.getBoundingClientRect();
    const relativeY = Math.min(Math.max(0, clientY - rect.top), Math.max(rect.height - 1, 0));
    const sectionAnchors = Array.from(
      itemsArea.querySelectorAll<HTMLElement>('[data-timeline-section-anchor="true"][data-timeline-section-id]'),
    )
      .map((element) => {
        const sectionId = element.dataset.timelineSectionId;
        if (!sectionId) {
          return null;
        }
        return {
          sectionId,
          top: Math.max(0, element.getBoundingClientRect().top - rect.top),
        };
      })
      .filter((anchor): anchor is { sectionId: string; top: number } => anchor !== null)
      .sort((a, b) => a.top - b.top);

    if (sectionAnchors.length > 0) {
      for (let index = sectionAnchors.length - 1; index >= 0; index -= 1) {
        if (relativeY >= sectionAnchors[index].top) {
          return sectionAnchors[index].sectionId;
        }
      }
      return sectionAnchors[0].sectionId;
    }

    const sectionHeight = Math.max(rect.height / timelines.length, 1);
    const sectionIndex = Math.min(timelines.length - 1, Math.floor(relativeY / sectionHeight));
    return timelines[sectionIndex]?.id ?? fallbackSectionId;
  };

  const calculateMsDelta = (baseDate: string, currentDate: string): number => {
    return new Date(currentDate).getTime() - new Date(baseDate).getTime();
  };

  const getShiftedRangeByMsDelta = (range: CreateDraft, deltaMs: number): CreateDraft => {
    const spanMs = Math.max(dateRange.end.getTime() - dateRange.start.getTime(), 1);
    const granularity = getSnapGranularity(spanMs);
    return {
      startDate: shiftDateByMs(range.startDate, deltaMs, granularity),
      endDate: range.endDate ? shiftDateByMs(range.endDate, deltaMs, granularity) : undefined,
    };
  };

  const stopAutoPan = () => {
    autoPanStrengthRef.current = 0;
    if (autoPanRafRef.current !== null) {
      window.cancelAnimationFrame(autoPanRafRef.current);
      autoPanRafRef.current = null;
    }
  };

  const updateAutoPanStrength = (clientX: number) => {
    const zoomSurface = zoomSurfaceRef.current;
    if (!zoomSurface) {
      autoPanStrengthRef.current = 0;
      return;
    }

    const rect = zoomSurface.getBoundingClientRect();
    const leftDistance = clientX - rect.left;
    const rightDistance = rect.right - clientX;

    if (leftDistance < AUTO_PAN_EDGE_THRESHOLD_PX) {
      autoPanStrengthRef.current = -(1 - (leftDistance / AUTO_PAN_EDGE_THRESHOLD_PX));
      return;
    }
    if (rightDistance < AUTO_PAN_EDGE_THRESHOLD_PX) {
      autoPanStrengthRef.current = 1 - (rightDistance / AUTO_PAN_EDGE_THRESHOLD_PX);
      return;
    }
    autoPanStrengthRef.current = 0;
  };

  const ensureMoveAutoPanLoop = () => {
    if (autoPanRafRef.current !== null) {
      return;
    }

    const tick = () => {
      autoPanRafRef.current = null;
      const pointerState = movePointerStateRef.current;
      const strength = autoPanStrengthRef.current;
      if (!pointerState || strength === 0) {
        return;
      }

      schedulePan(strength * AUTO_PAN_MAX_MS_PER_FRAME);
      autoPanRafRef.current = window.requestAnimationFrame(tick);
    };
    autoPanRafRef.current = window.requestAnimationFrame(tick);
  };

  const clearResizeInteraction = (pointerId?: number) => {
    resizePointerStateRef.current = null;
    setResizePreviewState(null);
    const zoomSurface = zoomSurfaceRef.current;
    if (!zoomSurface || pointerId === undefined) {
      return;
    }
    if (zoomSurface.hasPointerCapture?.(pointerId)) {
      zoomSurface.releasePointerCapture?.(pointerId);
    }
  };

  const clearMoveInteraction = (pointerId?: number) => {
    movePointerStateRef.current = null;
    setMovePreviewState(null);
    stopAutoPan();
    const zoomSurface = zoomSurfaceRef.current;
    if (!zoomSurface || pointerId === undefined) {
      return;
    }
    if (zoomSurface.hasPointerCapture?.(pointerId)) {
      zoomSurface.releasePointerCapture?.(pointerId);
    }
  };

  const startResize = (
    itemId: string,
    edge: 'start' | 'end',
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (isReadOnly) {
      return;
    }
    if (addMode !== 'none' || pendingCreateDraft || editingItemId) {
      return;
    }
    if (event.pointerType !== 'touch' && event.button !== 0) {
      return;
    }

    const targetItem = sectionItemById.get(itemId);
    if (!targetItem) {
      return;
    }

    creationPointerStateRef.current = null;
    setDragPreviewDraft(null);
    setHoverPreviewDraft(null);
    handleItemHover(null);
    cancelItemTitleEditing();

    const initialRange: CreateDraft = {
      startDate: targetItem.startDate,
      endDate: targetItem.endDate,
    };
    resizePointerStateRef.current = {
      pointerId: event.pointerId,
      sectionId: targetItem.sectionId,
      itemId,
      edge,
      initialRange,
    };
    setResizePreviewState({
      itemId,
      edge,
      sectionId: targetItem.sectionId,
      draft: initialRange,
    });
    zoomSurfaceRef.current?.setPointerCapture?.(event.pointerId);
  };

  const applyResizePointerMove = (event: React.PointerEvent<HTMLDivElement>): boolean => {
    const pointerState = resizePointerStateRef.current;
    if (!pointerState) {
      return false;
    }
    if (pointerState.pointerId !== event.pointerId) {
      return true;
    }

    const currentDate = dateFromClientX(event.clientX);
    if (!currentDate) {
      return true;
    }

    const anchorDate = pointerState.edge === 'start'
      ? (pointerState.initialRange.endDate ?? pointerState.initialRange.startDate)
      : pointerState.initialRange.startDate;
    const rawDraft = pointerState.edge === 'start'
      ? normalizeEditableRange(currentDate, anchorDate)
      : normalizeEditableRange(anchorDate, currentDate);

    const draft = resolveResizedRange(pointerState.initialRange, rawDraft);

    setResizePreviewState({
      itemId: pointerState.itemId,
      edge: pointerState.edge,
      sectionId: pointerState.sectionId,
      draft,
    });
    return true;
  };

  const commitResizePointer = (event: React.PointerEvent<HTMLDivElement>): boolean => {
    const pointerState = resizePointerStateRef.current;
    if (!pointerState) {
      return false;
    }
    if (pointerState.pointerId !== event.pointerId) {
      return true;
    }

    const nextRange = resizePreviewState?.itemId === pointerState.itemId
      ? resizePreviewState.draft
      : pointerState.initialRange;

    const resolvedRange = resolveResizedRange(pointerState.initialRange, nextRange);
    const commitEndDate = resolvedRange.endDate;

    const hasChanged = (
      pointerState.initialRange.startDate !== nextRange.startDate
      || (pointerState.initialRange.endDate ?? undefined) !== (commitEndDate ?? undefined)
    );
    if (hasChanged) {
      onItemDateRangeChange?.(pointerState.itemId, {
        sectionId: pointerState.sectionId,
        startDate: nextRange.startDate,
        endDate: commitEndDate,
      });
    }

    clearResizeInteraction(event.pointerId);
    return true;
  };

  const cancelResizePointer = (event: React.PointerEvent<HTMLDivElement>): boolean => {
    const pointerState = resizePointerStateRef.current;
    if (!pointerState) {
      return false;
    }
    if (pointerState.pointerId !== event.pointerId) {
      return true;
    }
    clearResizeInteraction(event.pointerId);
    return true;
  };

  const startMove = (itemId: string, event: React.PointerEvent<HTMLDivElement>) => {
    if (isReadOnly) {
      return;
    }
    if (addMode !== 'none' || pendingCreateDraft || resizePointerStateRef.current) {
      return;
    }
    if (event.pointerType !== 'touch' && event.button !== 0) {
      return;
    }

    const targetItem = sectionItemById.get(itemId);
    if (!targetItem) {
      return;
    }

    if (editingItemId) {
      commitItemTitleEditing();
    }

    creationPointerStateRef.current = null;
    setDragPreviewDraft(null);
    setHoverPreviewDraft(null);
    handleItemHover(null);

    const initialRange: CreateDraft = {
      startDate: targetItem.startDate,
      endDate: targetItem.endDate,
    };
    const grabDate = dateFromClientX(event.clientX) ?? targetItem.startDate;
    movePointerStateRef.current = {
      pointerId: event.pointerId,
      sectionId: targetItem.sectionId,
      itemId,
      initialRange,
      baseDate: grabDate,
      lastDate: grabDate,
      startClientX: event.clientX,
      startClientY: event.clientY,
      isDragging: false,
    };
  };

  const applyMovePointer = (event: React.PointerEvent<HTMLDivElement>): boolean => {
    const pointerState = movePointerStateRef.current;
    if (!pointerState) {
      return false;
    }
    if (pointerState.pointerId !== event.pointerId) {
      return true;
    }

    const distance = Math.hypot(
      event.clientX - pointerState.startClientX,
      event.clientY - pointerState.startClientY,
    );
    if (!pointerState.isDragging && distance < MOVE_DRAG_THRESHOLD_PX) {
      return true;
    }

    if (!pointerState.isDragging) {
      pointerState.isDragging = true;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setMovePreviewState({
        itemId: pointerState.itemId,
        sectionId: pointerState.sectionId,
        draft: pointerState.initialRange,
      });
    }

    const currentDate = dateFromClientX(event.clientX);
    if (!currentDate) {
      return true;
    }

    pointerState.lastDate = currentDate;
    const msDelta = calculateMsDelta(pointerState.baseDate, currentDate);
    const draft = getShiftedRangeByMsDelta(pointerState.initialRange, msDelta);
    const nextSectionId = resolveSectionIdByY(event.clientY) ?? pointerState.sectionId;
    setMovePreviewState({
      itemId: pointerState.itemId,
      sectionId: nextSectionId,
      draft,
    });
    updateAutoPanStrength(event.clientX);
    if (autoPanStrengthRef.current !== 0) {
      ensureMoveAutoPanLoop();
    } else {
      stopAutoPan();
    }
    return true;
  };

  const commitMovePointer = (event: React.PointerEvent<HTMLDivElement>): boolean => {
    const pointerState = movePointerStateRef.current;
    if (!pointerState) {
      return false;
    }
    if (pointerState.pointerId !== event.pointerId) {
      return true;
    }

    const nextRange = movePreviewState?.itemId === pointerState.itemId
      ? movePreviewState.draft
      : pointerState.initialRange;
    const nextSectionId = movePreviewState?.itemId === pointerState.itemId
      ? movePreviewState.sectionId
      : pointerState.sectionId;
    const hasChanged = (
      pointerState.sectionId !== nextSectionId
      || pointerState.initialRange.startDate !== nextRange.startDate
      || (pointerState.initialRange.endDate ?? undefined) !== (nextRange.endDate ?? undefined)
    );
    if (hasChanged) {
      onItemDateRangeChange?.(pointerState.itemId, {
        sectionId: nextSectionId,
        startDate: nextRange.startDate,
        endDate: nextRange.endDate,
      });
    }
    clearMoveInteraction(event.pointerId);
    return true;
  };

  const cancelMovePointer = (event: React.PointerEvent<HTMLDivElement>): boolean => {
    const pointerState = movePointerStateRef.current;
    if (!pointerState) {
      return false;
    }
    if (pointerState.pointerId !== event.pointerId) {
      return true;
    }
    clearMoveInteraction(event.pointerId);
    return true;
  };

  const handleCreatePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isReadOnly) {
      return;
    }
    if (resizePointerStateRef.current || movePointerStateRef.current) {
      return;
    }

    if (addMode === 'none' || pendingCreateDraft || editingItemId) {
      return;
    }

    if (event.pointerType !== 'touch' && event.button !== 0) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-timeline-item-root="true"]')) {
      return;
    }

    const startDate = dateFromClientX(event.clientX);
    const sectionId = resolveSectionIdByY(event.clientY);
    if (!startDate || !sectionId) {
      return;
    }

    event.preventDefault();
    cancelItemTitleEditing();
    handleItemHover(null);

    creationPointerStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startDate,
      sectionId,
      lastDate: startDate,
      isDragging: false,
    };

    const previewDraft = addMode === 'duration'
      ? { sectionId, startDate, endDate: startDate }
      : { sectionId, startDate };
    setHoverPreviewDraft(null);
    setDragPreviewDraft(previewDraft);
    setPreviewClientY(event.clientY);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCreatePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isReadOnly) {
      setHoverPreviewDraft(null);
      return;
    }
    if (resizePointerStateRef.current || movePointerStateRef.current) {
      return;
    }

    const pointerState = creationPointerStateRef.current;
    if (!pointerState) {
      if (addMode === 'none' || pendingCreateDraft || editingItemId) {
        setHoverPreviewDraft(null);
        return;
      }

      const hoverDate = dateFromClientX(event.clientX);
      const hoverSectionId = resolveSectionIdByY(event.clientY);
      if (!hoverDate || !hoverSectionId) {
        setHoverPreviewDraft(null);
        return;
      }

      setHoverPreviewDraft(
        addMode === 'duration'
          ? { sectionId: hoverSectionId, startDate: hoverDate, endDate: hoverDate }
          : { sectionId: hoverSectionId, startDate: hoverDate },
      );
      return;
    }

    if (pointerState.pointerId !== event.pointerId) {
      return;
    }

    const currentDate = dateFromClientX(event.clientX);
    if (!currentDate) {
      return;
    }

    const distance = Math.hypot(
      event.clientX - pointerState.startClientX,
      event.clientY - pointerState.startClientY,
    );
    const willStartDragging = !pointerState.isDragging && distance >= CREATE_DRAG_THRESHOLD_PX;
    if (willStartDragging) {
      setPreviewClientY(pointerState.startClientY);
    } else if (!pointerState.isDragging) {
      setPreviewClientY(event.clientY);
    }
    pointerState.lastDate = currentDate;
    pointerState.isDragging = pointerState.isDragging || distance >= CREATE_DRAG_THRESHOLD_PX;

    if (addMode === 'duration') {
      setDragPreviewDraft({
        sectionId: pointerState.sectionId,
        ...normalizeDurationRange(pointerState.startDate, currentDate),
      });
      return;
    }

    setDragPreviewDraft({ sectionId: pointerState.sectionId, startDate: pointerState.startDate });
  };

  const updateHoverPreviewByPointer = (clientX: number, clientY: number) => {
    if (
      isReadOnly
      || addMode === 'none'
      || pendingCreateDraft
      || editingItemId
      || creationPointerStateRef.current
      || resizePointerStateRef.current
      || movePointerStateRef.current
    ) {
      setHoverPreviewDraft(null);
      return;
    }

    const hoverDate = dateFromClientX(clientX);
    const hoverSectionId = resolveSectionIdByY(clientY);
    if (!hoverDate || !hoverSectionId) {
      setHoverPreviewDraft(null);
      return;
    }

    setHoverPreviewDraft(
      addMode === 'duration'
        ? { sectionId: hoverSectionId, startDate: hoverDate, endDate: hoverDate }
        : { sectionId: hoverSectionId, startDate: hoverDate },
    );
  };

  const finalizeCreatePointer = (event: React.PointerEvent<HTMLDivElement>) => {
    if (resizePointerStateRef.current || movePointerStateRef.current) {
      return;
    }

    const pointerState = creationPointerStateRef.current;
    if (!pointerState || pointerState.pointerId !== event.pointerId) {
      return;
    }

    const endDate = pointerState.lastDate;
    const nextDraft = (() => {
      if (addMode === 'milestone') {
        return { sectionId: pointerState.sectionId, startDate: pointerState.startDate };
      }
      if (!pointerState.isDragging) {
        return { sectionId: pointerState.sectionId, startDate: pointerState.startDate, endDate: pointerState.startDate };
      }
      return { sectionId: pointerState.sectionId, ...normalizeDurationRange(pointerState.startDate, endDate) };
    })();

    creationPointerStateRef.current = null;
    setDragPreviewDraft(null);
    setHoverPreviewDraft(null);
    setPendingCreateDraft(nextDraft);
    setPendingCreateTitle('');

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleCreatePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
    if (resizePointerStateRef.current || movePointerStateRef.current) {
      return;
    }

    const pointerState = creationPointerStateRef.current;
    if (!pointerState || pointerState.pointerId !== event.pointerId) {
      return;
    }

    creationPointerStateRef.current = null;
    setDragPreviewDraft(null);
    setHoverPreviewDraft(null);
    setPreviewClientY(null);

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleCreatePointerLeave = () => {
    if (creationPointerStateRef.current) {
      return;
    }
    setHoverPreviewDraft(null);
    setPreviewClientY(null);
  };

  const handleCreatePointerEnter = (event: React.PointerEvent<HTMLDivElement>) => {
    setPreviewClientY(event.clientY);
    updateHoverPreviewByPointer(event.clientX, event.clientY);
  };

  const handleCreateMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    setPreviewClientY(event.clientY);
    updateHoverPreviewByPointer(event.clientX, event.clientY);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      touchPanStateRef.current = null;
      pinchStateRef.current = {
        distance: calculateDistance(event.touches),
      };
      return;
    }

    if (event.touches.length === 1) {
      pinchStateRef.current = null;

      if (
        addMode !== 'none'
        || pendingCreateDraft
        || editingItemId
        || resizePointerStateRef.current
        || movePointerStateRef.current
      ) {
        touchPanStateRef.current = null;
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-timeline-item-root="true"]')) {
        touchPanStateRef.current = null;
        return;
      }

      touchPanStateRef.current = {
        startClientX: event.touches[0].clientX,
        lastClientX: event.touches[0].clientX,
        isDragging: false,
      };
      return;
    }

    touchPanStateRef.current = null;
    pinchStateRef.current = null;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2 && pinchStateRef.current) {
      event.preventDefault();
      const currentDistance = calculateDistance(event.touches);
      if (currentDistance <= 0 || pinchStateRef.current.distance <= 0) {
        return;
      }

      const scale = currentDistance / pinchStateRef.current.distance;
      pinchStateRef.current.distance = currentDistance;
      scheduleZoom(scale);
      return;
    }

    if (event.touches.length === 1 && touchPanStateRef.current) {
      const currentX = event.touches[0].clientX;

      if (!touchPanStateRef.current.isDragging) {
        const distance = Math.abs(currentX - touchPanStateRef.current.startClientX);
        if (distance < TOUCH_PAN_THRESHOLD_PX) {
          return;
        }
        touchPanStateRef.current.isDragging = true;
      }

      event.preventDefault();
      const deltaX = touchPanStateRef.current.lastClientX - currentX;
      touchPanStateRef.current.lastClientX = currentX;

      const interactionWidth = Math.max(zoomSurfaceRef.current?.clientWidth ?? 0, 1);
      const spanMs = dateRange.end.getTime() - dateRange.start.getTime();
      const deltaMs = (deltaX / interactionWidth) * spanMs;
      schedulePan(deltaMs);
      return;
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length < 2) {
      pinchStateRef.current = null;
    }
    if (event.touches.length === 0) {
      touchPanStateRef.current = null;
    }
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (event.ctrlKey) {
      event.preventDefault();
      const scale = Math.exp(-event.deltaY * 0.01);
      scheduleZoom(scale);
      return;
    }

    const horizontalDelta = Math.abs(event.deltaX) >= Math.abs(event.deltaY)
      ? event.deltaX
      : 0;
    if (horizontalDelta === 0) {
      return;
    }

    event.preventDefault();
    const interactionWidth = Math.max(zoomSurfaceRef.current?.clientWidth ?? 0, 1);
    const spanMs = dateRange.end.getTime() - dateRange.start.getTime();
    const deltaMs = (horizontalDelta / interactionWidth) * spanMs;
    schedulePan(deltaMs);
  };

  const handleZoomSurfaceScroll = (event: React.UIEvent<HTMLDivElement>) => {
    setHorizontalScrollLeft(event.currentTarget.scrollLeft);
  };

  const handleAddModeClick = (mode: Exclude<AddMode, 'none'>) => {
    if (isReadOnly) {
      return;
    }
    setAddMode(prev => (prev === mode ? 'none' : mode));
  };

  useEffect(() => {
    const wasReadOnly = prevReadOnlyRef.current;
    prevReadOnlyRef.current = isReadOnly;
    if (!isReadOnly || wasReadOnly) {
      return;
    }

    creationPointerStateRef.current = null;
    clearResizeInteraction();
    clearMoveInteraction();
    cancelItemTitleEditing();
    cancelPendingCreate();
    setHoverPreviewDraft(null);
    handleItemHover(null);
  }, [isReadOnly, handleItemHover]);

  useEffect(() => {
    return () => {
      if (zoomRafRef.current !== null) {
        window.cancelAnimationFrame(zoomRafRef.current);
      }
      if (panRafRef.current !== null) {
        window.cancelAnimationFrame(panRafRef.current);
      }
      stopAutoPan();
    };
  }, []);

  useEffect(() => {
    if (addMode === 'none' && !pendingCreateDraft) {
      return;
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      cancelPendingCreate();
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [addMode, pendingCreateDraft]);

  useEffect(() => {
    if (!editingItemId) {
      return;
    }

    if (!itemsWithColors.some(item => item.id === editingItemId)) {
      cancelItemTitleEditing();
    }
  }, [editingItemId, itemsWithColors]);

  useEffect(() => {
    if (addMode === 'none') {
      creationPointerStateRef.current = null;
      setDragPreviewDraft(null);
      setHoverPreviewDraft(null);
      setPreviewClientY(null);
      return;
    }

    clearResizeInteraction();
    clearMoveInteraction();
    cancelItemTitleEditing();
    handleItemHover(null);
  }, [addMode, handleItemHover]);

  return {
    horizontalScrollLeft,
    editingItemId,
    setEditingItemId,
    editingTitleDraft,
    setEditingTitleDraft,
    addMode,
    setAddMode,
    pendingCreateDraft,
    setPendingCreateDraft,
    pendingCreateTitle,
    setPendingCreateTitle,
    dragPreviewDraft,
    hoverPreviewDraft,
    resizePreviewState,
    movePreviewState,
    previewClientY,
    isCreateFlowActive,
    isResizeFlowActive,
    isMoveFlowActive,
    startItemTitleEditing,
    cancelItemTitleEditing,
    commitItemTitleEditing,
    cancelPendingCreate,
    commitPendingCreate,
    startResize,
    startMove,
    handleAddModeClick,
    handleZoomSurfaceScroll,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheel,
    handleCreatePointerDown,
    handleCreatePointerMove,
    finalizeCreatePointer,
    handleCreatePointerCancel,
    handleCreatePointerEnter,
    handleCreatePointerLeave,
    handleCreateMouseMove,
    applyResizePointerMove,
    commitResizePointer,
    cancelResizePointer,
    applyMovePointer,
    commitMovePointer,
    cancelMovePointer,
  };
};
